/**
 * Volet — un volet ancré sous un bouton, au contenu libre (lot 5 du catalogue, 01-11-2026) :
 * les filtres d'une liste, le détail d'un état, un petit formulaire. Le popover. Même mécanique
 * que le `Menu` : porté sur <body> dans le thème de l'ancre, en pixels de mise en page (le zoom
 * du body, cf portail.ts), jamais hors de l'écran (au-dessus de l'ancre si la place manque
 * dessous), Échap et un clic dehors ferment, le focus entre dans le volet et revient à l'ancre.
 *
 *   const v = useVolet();
 *   <Button ref={v.ancre} onClick={v.basculer} aria-expanded={v.ouvert}>Filtrer</Button>
 *   <Volet ancre={v.ancre} ouvert={v.ouvert} onClose={v.fermer} ariaLabel="Filtres">…</Volet>
 *
 * Déclaratif : le contenu est rendu par l'appelant, il suit son état comme n'importe quoi.
 * `largeur="ancre"` prend la largeur du bouton ; `alignement="droite"` colle le volet au bord
 * droit de l'ancre (un bouton en bout de barre) ; `statique` le pose dans la page (le catalogue).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { useFermerAEchap, useFocusDansLaFenetre } from "./ouverture";
import { cadreDuTheme, rectVue, vueDuCorps, type CadreTheme } from "./portail";
import { cn } from "./utils";
import "./champs.css";
import "./ouverture.css";

export interface VoletProps {
  ouvert: boolean;
  onClose: () => void;
  /** Le bouton qui l'ouvre : une ref, ou l'élément. */
  ancre: RefObject<HTMLElement | null> | HTMLElement | null;
  /** `gauche` (défaut) : le volet part du bord gauche de l'ancre ; `droite` : il finit à son bord droit. */
  alignement?: "gauche" | "droite";
  /** En px, ou `"ancre"` pour la largeur du bouton. Sinon, celle du contenu (220 px au moins). */
  largeur?: number | "ancre";
  /** Défaut `md` (12 px) ; `aucun` pour une liste qui gère ses marges. */
  remplissage?: "aucun" | "md";
  ariaLabel?: string;
  children: ReactNode;
  /** Posé dans la page — le catalogue. */
  statique?: boolean;
  className?: string;
}

const ECART = 6;
const MARGE = 8;

function elementDe(ancre: VoletProps["ancre"]): HTMLElement | null {
  if (!ancre) return null;
  return "current" in ancre ? ancre.current : ancre;
}

export function Volet({ ouvert, onClose, ancre, alignement = "gauche", largeur, remplissage = "md", ariaLabel, children, statique = false, className }: VoletProps) {
  const volet = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width?: number; sens: "bas" | "haut" } | null>(null);
  const [cadre, setCadre] = useState<CadreTheme>({ sombre: false });
  const vivant = ouvert && !statique;

  useFermerAEchap(vivant, onClose);
  useFocusDansLaFenetre(vivant, volet);

  // Sous l'ancre, au-dessus si la place manque ; jamais hors de l'écran ; dans son thème.
  useLayoutEffect(() => {
    if (!vivant) return;
    const el = elementDe(ancre);
    setCadre(cadreDuTheme(el));
    const { zoom, largeur: W, hauteur: H } = vueDuCorps();
    const a = rectVue(el, zoom);
    const v = rectVue(volet.current, zoom);
    if (!a || !v) return;
    const w = largeur === "ancre" ? a.width : typeof largeur === "number" ? largeur : v.width;
    let sens: "bas" | "haut" = "bas";
    let top = a.bottom + ECART;
    if (top + v.height > H - MARGE && a.top - ECART - v.height >= MARGE) {
      sens = "haut";
      top = a.top - ECART - v.height;
    }
    const left = alignement === "droite" ? a.right - w : a.left;
    setPos({
      left: Math.max(MARGE, Math.min(left, W - w - MARGE)),
      top: Math.max(MARGE, top),
      width: largeur !== undefined ? w : undefined,
      sens,
    });
  }, [vivant, ancre, alignement, largeur]);

  // Un clic dehors (ni dans le volet, ni sur l'ancre), un défilement ailleurs, un
  // redimensionnement, la fenêtre qui perd le focus : fermé.
  const fermer = useRef(onClose);
  fermer.current = onClose;
  useEffect(() => {
    if (!vivant) return;
    const dedans = (cible: EventTarget | null) =>
      cible instanceof Node && (volet.current?.contains(cible) || elementDe(ancre)?.contains(cible));
    const auClic = (e: PointerEvent) => {
      if (!dedans(e.target)) fermer.current();
    };
    const auDefilement = (e: Event) => {
      if (!(e.target instanceof Node && volet.current?.contains(e.target))) fermer.current();
    };
    const auReste = () => fermer.current();
    window.addEventListener("pointerdown", auClic);
    window.addEventListener("scroll", auDefilement, true);
    window.addEventListener("resize", auReste);
    window.addEventListener("blur", auReste);
    return () => {
      window.removeEventListener("pointerdown", auClic);
      window.removeEventListener("scroll", auDefilement, true);
      window.removeEventListener("resize", auReste);
      window.removeEventListener("blur", auReste);
    };
  }, [vivant, ancre]);

  if (!ouvert) return null;

  const contenu = (
    <div
      ref={volet}
      role="dialog"
      aria-label={ariaLabel}
      tabIndex={-1}
      data-sens={pos?.sens}
      data-remplissage={remplissage === "aucun" ? "aucun" : undefined}
      className={cn("vo ch-arrondi-menu", statique && "vo-statique", className)}
      style={
        statique
          ? { width: typeof largeur === "number" ? largeur : undefined }
          : { left: pos?.left ?? 0, top: pos?.top ?? 0, width: pos?.width, visibility: pos ? undefined : "hidden" }
      }
    >
      {children}
    </div>
  );
  if (statique) return contenu;
  return createPortal(
    <div className={cadre.sombre ? "dark" : undefined} data-theme={cadre.theme}>
      {contenu}
    </div>,
    document.body,
  );
}

/** L'état d'un volet et la ref de son bouton : `ouvrir`, `fermer`, `basculer`. */
export function useVolet<T extends HTMLElement = HTMLButtonElement>(): {
  ancre: RefObject<T | null>;
  ouvert: boolean;
  ouvrir: () => void;
  fermer: () => void;
  basculer: () => void;
} {
  const ancre = useRef<T | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const ouvrir = useCallback(() => setOuvert(true), []);
  const fermer = useCallback(() => setOuvert(false), []);
  const basculer = useCallback(() => setOuvert((o) => !o), []);
  return { ancre, ouvert, ouvrir, fermer, basculer };
}
